import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    color = "color",
    heading = "heading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.color]: string;
        [fields.heading]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    overlayWrapperStyles?: InjectableCss;
    titleStyles?: InjectableCss;
}

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const CustomTitleWrapper = styled.div<{
    backgroundImage: string;
    color: string;
    checkBackground: string;
}>`
    background: ${props => (props.checkBackground === "image" ? `url(${props.backgroundImage})` : props.color)};
    background-size: cover;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 30px;
    min-height: 95px;
    width: 100%;
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    // min-height: 180px;
                `,
                null,
                null,
                null,
            ],
            "max",
        )}
`;

const FisscoCustomPageTitle: React.FunctionComponent<Props> = ({ pageTitle, fields }: Props) => {
    const overlayWrapperStyles = {
        // ${backgroundStyles}
        css: css`
            width: calc(100vw - 20px + 6px);
            margin-left: calc(-50vw + 50% + 5px);
            margin-top: -15px;

            .banner-title-wrapper {
                width: 100%;
                max-width: 1440px;
                margin-inline: auto;
                padding: 4rem 2.5rem;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0 1.5rem;
                        `,
                        css`
                            padding: 0 1.5rem;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 0px;
                        `,

                        css`
                            margin-top: -15px;
                        `,
                        css`
                            margin-top: -15px;
                        `,
                        css`
                            margin-top: -15px;
                        `,
                        css`
                            margin-top: -15px;
                        `,
                    ],
                    "min",
                )}
        `,
    };

    const titleStyles = {
        css: css`
            color: #fff;
            margin: 0;
            font-size: 48px !important;
            text-transform: uppercase !important;
            line-height: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 22px !important;
                    `,
                    css`
                        font-size: 22px !important;
                    `,
                    css`
                        font-size: 30px !important;
                    `,
                    css`
                        font-size: 36px !important;
                    `,
                    null,
                ])}
        `,
    };
    return (
        <StyledWrapper className="banner-wrapper" {...overlayWrapperStyles}>
            <CustomTitleWrapper backgroundImage={fields.image} color={fields.color} checkBackground={fields.background}>
                <StyledWrapper className="banner-title-wrapper">
                    <Typography variant="h2" as="h1" data-test-selector="CustomPageTitle" {...titleStyles}>
                        {fields.heading || fields.overrideTitle || pageTitle}
                    </Typography>
                </StyledWrapper>
            </CustomTitleWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(FisscoCustomPageTitle),
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.color,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "black",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Custom Page Title",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
